import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Eye,
  HardDrive,
  Calendar,
  UserRound,
  FileText,
  Building2,
  FolderOpen,
  UserCircle2,
} from 'lucide-react';
import { enUS, fr } from 'date-fns/locale';
import { format } from 'date-fns';
import { cn } from '@/utils/cn';
import { Document } from '../types';
import { DocumentCategoryBadge } from './DocumentCategoryBadge';
import { DocumentActions } from './DocumentActions';
import { canPreview, formatBytes, getMimeTypeIcon } from '../utils';

interface DocumentCardProps {
  document: Document;
  onPreview?: (uuid: string) => void;
  onDownload?: (document: Document) => void;
  onDelete?: (uuid: string) => void;
  onEdit?: (document: Document) => void;
}

export const DocumentCard: React.FC<DocumentCardProps> = ({
  document,
  onPreview,
  onDownload,
  onDelete,
  onEdit,
}) => {
  const { t, i18n } = useTranslation();

  const dateLocale = useMemo(
    () => (i18n.language.startsWith('fr') ? fr : enUS),
    [i18n.language]
  );

  // Get icon based on document type
  const getDocumentIcon = () => {
    if (document.type === 'text') return '📝';
    if (document.type === 'credentials') return '🔐';
    if (document.thumbnailUrl) {
      return null; // Use thumbnail
    }
    return (
      <span className="text-2xl">
        {getMimeTypeIcon(document.mimeType || 'application/octet-stream')}
      </span>
    );
  };

  const iconElement = getDocumentIcon();

  // Déterminer si le document peut être prévisualisé
  const canShowPreview =
    document.type === 'text' ||
    document.type === 'credentials' ||
    (document.mimeType && canPreview(document.mimeType));

  const contextInfo = (() => {
    switch (document.context) {
      case 'domain':
        return {
          icon: Building2,
          label: t('documents.contexts.domain'),
          value: document.domain?.name ?? '—',
        };
      case 'folder':
        return {
          icon: FolderOpen,
          label: t('documents.contexts.folder'),
          value: document.folder?.name ?? '—',
        };
      case 'employee':
        return {
          icon: UserCircle2,
          label: t('documents.contexts.employee'),
          value: document.employee
            ? `${document.employee.firstName} ${document.employee.lastName}`.trim()
            : '—',
        };
      default:
        return null;
    }
  })();

  const handlePreview = () => {
    if (canShowPreview && onPreview) {
      onPreview(document.uuid);
    }
  };

  return (
    <div className="group flex items-start gap-4 rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
      <button
        type="button"
        onClick={handlePreview}
        disabled={!canShowPreview}
        className={cn(
          'group/preview focus-visible:ring-primary-500/40 relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-neutral-200 bg-neutral-50 transition-all focus-visible:ring-2 focus-visible:outline-none',
          canShowPreview
            ? 'hover:border-primary-400 hover:bg-primary-50 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60'
            : 'cursor-default opacity-70'
        )}
      >
        {document.thumbnailUrl && typeof iconElement !== 'string' ? (
          <img
            src={document.thumbnailUrl}
            alt={document.name}
            className="h-full w-full object-cover transition-transform group-hover/preview:scale-105"
          />
        ) : iconElement ? (
          typeof iconElement === 'string' ? (
            <span className="text-2xl">{iconElement}</span>
          ) : (
            iconElement
          )
        ) : (
          <span className="text-2xl">📄</span>
        )}
        {canShowPreview && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-xl bg-neutral-900/50 opacity-0 transition-opacity group-hover/preview:opacity-100">
            <Eye className="h-5 w-5 text-white" />
          </div>
        )}
      </button>

      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1 space-y-1.5">
            <DocumentCategoryBadge category={document.category} size="sm" />
            <h3 className="truncate text-base font-semibold text-neutral-900">
              {document.name}
            </h3>
            {document.description && (
              <p className="line-clamp-2 text-sm text-neutral-600">
                {document.description}
              </p>
            )}
          </div>
          <DocumentActions
            document={document}
            onPreview={onPreview}
            onDownload={onDownload}
            onDelete={onDelete}
            onEdit={onEdit}
          />
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-neutral-500">
          {contextInfo && contextInfo.value !== '—' && (
            <span className="inline-flex w-full items-center gap-1">
              <contextInfo.icon className="h-3.5 w-3.5 text-neutral-400" />
              <span className="font-medium text-neutral-600">
                {contextInfo.label} · {contextInfo.value}
              </span>
            </span>
          )}

          {document.size && (
            <span className="inline-flex items-center gap-1">
              <HardDrive className="h-3.5 w-3.5 text-neutral-400" />
              <span>{formatBytes(document.size)}</span>
            </span>
          )}

          {document.metadata?.pageCount && (
            <span className="inline-flex items-center gap-1">
              <FileText className="h-3.5 w-3.5 text-neutral-400" />
              <span>
                {t('documents.units.page', {
                  count: document.metadata.pageCount,
                })}
              </span>
            </span>
          )}

          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5 text-neutral-400" />
            <span>
              {format(new Date(document.createdAt), 'dd MMM yyyy', {
                locale: dateLocale,
              })}
            </span>
          </span>

          {document.uploadedByDisplayName && (
            <span className="inline-flex items-center gap-1">
              <UserRound className="h-3.5 w-3.5 text-neutral-400" />
              <span>{document.uploadedByDisplayName}</span>
            </span>
          )}
        </div>

        {document.tags && document.tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {document.tags.map((tag, index) => (
              <span
                key={`${tag}-${index}`}
                className="inline-flex items-center rounded-full bg-neutral-100 px-2.5 py-1 text-[11px] font-medium text-neutral-600"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
